import { TICKS_PER_LOOP } from '@arpeggi/shared'
import chroma from 'chroma-js'
import { observer } from 'mobx-react-lite'
import {
  ComponentProps,
  FC,
  MouseEventHandler,
  PointerEventHandler,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef
} from 'react'
import { Arp2Utils } from '../../../../client/SongUtils'
import { LOOP_COLORS, styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import { INoteStore } from '../../../../stores/song/loop/NoteStore'
import { IInstrumentStemStore } from '../../../../stores/song/stem/InstrumentStemStore'
import { undoManager } from '../../../../stores/StudioStore'
import { clampToRoundValue, clampToValue } from '../../../core/Utils'
import KeyScaleManager from './KeyScaleManager'
import PianoRollStateManager, { IPianoRollState } from './PianoRollStateManager'
import {
  KEYBOARD_WIDTH,
  NOTE_HEIGHT,
  NOTE_LABELS,
  NOTE_TAIL_WIDTH,
  useCurrentLoop,
  usePianoRollState
} from './PianoRollUtils'
import SnapManager from './SnapManager'
import { SCROLLBAR_SIZE } from './VerticalScrollBox'

interface INoteAreaData {
  scale: number
  x: number
}

const DEFAULT_BORDER_COLOR = '#5263F4'
const DEFAULT_FILL_COLOR = 'rgba(82, 99, 244, 0.7)'

const NoteBody = styled('div', {
  cursor: 'pointer',
  position: 'absolute',
  height: NOTE_HEIGHT,
  boxSizing: 'border-box',
  borderRadius: '2px',
  borderWidth: '1px',
  paddingLeft: '3px',
  overflow: 'hidden',
  textOverflow: 'clip',
  fontSize: '9px',
  lineHeight: '14px',
  fontWeight: '$medium',
  fontFamily: '$dm',
  borderStyle: 'solid',
  borderColor: DEFAULT_BORDER_COLOR,
  background: DEFAULT_FILL_COLOR,
  color: '$gray01',
  boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
  '&.transition': {
    cursor: 'inherit',
    transition: 'all 50ms ease'
  },
  '&.transition *': {
    cursor: 'inherit !important'
  }
})

const NoteTail = styled('div', {
  width: NOTE_TAIL_WIDTH,
  position: 'absolute',
  right: '0px',
  top: '0px',
  bottom: '0px',
  cursor: 'col-resize'
})

const DRAG_THRESHOLD = 5

const DEFAULT_NOTE_SIZE = { current: TICKS_PER_LOOP / 16 }

const updatePosition = (container: HTMLDivElement, note: INoteStore, state: IPianoRollState) => {
  const { tickWidthPx } = state
  container.style.cssText = `
    left: ${note.start * tickWidthPx}px;
    width: ${note.duration * tickWidthPx}px;
    top: ${KeyScaleManager.noteToY(note.note)}px;
  `
}

const Note: FC<{ note: INoteStore } & ComponentProps<typeof NoteBody>> = observer(
  ({ note, ...props }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const { studio } = useTreeState()
    const loop = useCurrentLoop()
    useLayoutEffect(() => {
      const id = note.uniqueId
      PianoRollStateManager.subscribe(
        id,
        (state) => {
          updatePosition(containerRef.current, note, state)
        },
        false
      )
      return () => PianoRollStateManager.unsubscribe(id)
    }, [])

    useEffect(() => {
      updatePosition(containerRef.current, note, PianoRollStateManager.state)
    }, [note.start, note.duration, note.note])

    const onMouseDown: PointerEventHandler<HTMLDivElement> = (evt) => {
      evt.stopPropagation()
      let dragging = false
      const startNote = note.note
      const duration = note.duration
      const startNoteStart = note.start
      const startX = evt.clientX
      const startY = evt.clientY
      containerRef.current.classList.add('transition')
      document.body.style.setProperty('cursor', 'grabbing', 'important')
      undoManager.startGroup(() => {
        studio.disableAudioUpdates()
        const handleMove = (evt: PointerEvent) => {
          const deltaX = evt.clientX - startX
          const deltaY = evt.clientY - startY
          if (Math.sqrt(deltaX * deltaX + deltaY * deltaY) > DRAG_THRESHOLD) dragging = true
          if (!dragging) return
          const diffX = Math.round(deltaX / PianoRollStateManager.state.tickWidthPx)
          const { snap, resolution } = SnapManager.state
          const gridSize = TICKS_PER_LOOP / Arp2Utils.getLengthOfRes(resolution)
          let noteStart = Math.max(startNoteStart + diffX, 0)
          if (TICKS_PER_LOOP < noteStart + duration) noteStart = TICKS_PER_LOOP - duration
          const finalStart = snap ? clampToRoundValue(noteStart, gridSize) : noteStart
          note.updateStart(finalStart)
          const y = KeyScaleManager.noteToY(startNote) + deltaY
          const finalNote = KeyScaleManager.yToNote(y)
          note.updateNote(finalNote)
        }
        window.addEventListener('pointermove', handleMove)
        window.addEventListener(
          'pointerup',
          (evt) => {
            evt.stopPropagation()
            document.body.style.removeProperty('cursor')
            containerRef.current.classList.remove('transition')
            window.removeEventListener('pointermove', handleMove)
            if (!dragging) loop.melodicLoop.notes.removeNotes([note.uniqueId])
            dragging = false
            studio.enableAudioUpdates()
            undoManager.stopGroup()
          },
          { once: true }
        )
      })
    }

    const onTailMouseDown: PointerEventHandler<HTMLDivElement> = (evt) => {
      evt.stopPropagation()
      const startDuration = note.duration
      const noteStart = note.start
      const startX = evt.clientX
      document.body.style.setProperty('cursor', 'col-resize', 'important')
      containerRef.current.classList.add('transition')
      undoManager.startGroup(() => {
        studio.disableAudioUpdates()
        const handleMove = (evt: PointerEvent) => {
          const x = evt.clientX
          const diffX = Math.round((x - startX) / PianoRollStateManager.state.tickWidthPx)
          const { snap, resolution } = SnapManager.state
          const gridSize = TICKS_PER_LOOP / Arp2Utils.getLengthOfRes(resolution)
          let duration = startDuration + diffX
          if (TICKS_PER_LOOP < noteStart + duration) duration = TICKS_PER_LOOP - noteStart
          const newDuration = snap ? clampToRoundValue(duration, gridSize) : duration
          note.updateDuration(newDuration)
          DEFAULT_NOTE_SIZE.current = newDuration
        }
        window.addEventListener('pointermove', handleMove)
        window.addEventListener(
          'pointerup',
          (evt) => {
            evt.stopPropagation()
            containerRef.current.classList.remove('transition')
            document.body.style.removeProperty('cursor')
            window.removeEventListener('pointermove', handleMove)
            studio.enableAudioUpdates()
            undoManager.stopGroup()
          },
          { once: true }
        )
      })
    }
    return (
      <NoteBody ref={containerRef} onPointerDown={onMouseDown} {...props}>
        {NOTE_LABELS[note.note % 12] + Math.floor(note.note / 12)}
        <NoteTail onPointerDown={onTailMouseDown} />
      </NoteBody>
    )
  }
)

const NoteAreaContainer = styled('div', {
  position: 'absolute',
  left: KEYBOARD_WIDTH,
  right: SCROLLBAR_SIZE,
  top: 0,
  bottom: 0,
  overflow: 'hidden'
})

const NotesArea: FC<PropsWithChildren<ComponentProps<typeof NoteAreaContainer>>> = ({
  children,
  ...props
}) => {
  const containerRef = usePianoRollState()
  return (
    <NoteAreaContainer ref={containerRef} {...props}>
      {children}
    </NoteAreaContainer>
  )
}

const Notes: FC = () => {
  const { studio } = useTreeState()
  const { selectedStemIndex, selectedSection, song } = studio
  const stem = song.stems[selectedStemIndex] as IInstrumentStemStore
  const { melodicSample } = stem
  const loop = stem.getLoopAtSectionOrNull(selectedSection)
  const { darker, base } = useMemo(() => {
    if (!loop) return { base: '', darker: '' }
    const base = chroma(LOOP_COLORS[stem.colorIndex % LOOP_COLORS.length])
    const darker = base.darken(0.8).saturate(0.2)
    return { base: base.hex(), darker: darker.hex() }
  }, [stem.colorIndex, loop])
  if (!loop) return null
  const noteCollection = loop.melodicLoop.notes
  const { notes } = noteCollection
  const handleClick: MouseEventHandler<HTMLDivElement> = (evt) => {
    evt.stopPropagation()
    window.addEventListener(
      'pointerup',
      (_evt) => {
        _evt.stopPropagation()
        const target = evt.target as HTMLDivElement
        const rect = target.getBoundingClientRect()
        const relativeX = _evt.clientX - rect.left
        const relativeY = _evt.clientY - rect.top
        const { tickWidthPx } = PianoRollStateManager.state
        const note = KeyScaleManager.yToNote(relativeY, true)
        const { snap, resolution } = SnapManager.state
        let start = relativeX / tickWidthPx
        const noteSizeInTicks = TICKS_PER_LOOP / Arp2Utils.getLengthOfRes(resolution)
        if (snap) start = clampToValue(start, noteSizeInTicks)
        let duration = DEFAULT_NOTE_SIZE.current
        if (duration + start > TICKS_PER_LOOP) duration = TICKS_PER_LOOP - start
        studio.playSampleWithEffects(melodicSample, stem.getStemGain(), note)
        noteCollection.addNote({ start, duration, note })
      },
      { once: true }
    )
  }
  return (
    <NotesArea onPointerDown={handleClick}>
      {notes.map((note, i) => (
        <Note
          note={note}
          key={note.uniqueId}
          css={{ borderColor: darker, background: base, color: darker }}
        />
      ))}
    </NotesArea>
  )
}

export default observer(Notes)
